import { z } from "zod/v4";

// The three fixed categories surfaced to marketers. The LLM generates cards
// for each in a single call; these labels are also the keys used in the
// persisted suggestions object.
export const SUGGESTION_CATEGORIES = [
  "Paid Media",
  "Lifecycle Marketing",
  "Customer Analysis",
] as const;

export type SuggestionCategory = (typeof SUGGESTION_CATEGORIES)[number];

// The icon a generated card may carry; each maps to a concrete glyph in the
// app's SUGGESTION_ICON_COMPONENTS map.
export const SUGGESTION_ICONS = [
  "ads",
  "lifecycle",
  "performance",
  "insights",
  "debugger",
  "find",
  "audience",
  "journey",
] as const;

export type SuggestionIcon = (typeof SUGGESTION_ICONS)[number];

export const MIN_SUGGESTIONS_PER_CATEGORY = 3;

export const suggestionCardSchema = z.object({
  headline: z
    .string()
    .min(1)
    .describe(
      "Short label rendered on the suggestion chip — a few words naming the angle the prompt explores.",
    ),
  prompt: z
    .string()
    .min(1)
    .describe(
      "The prompt body sent to the agent verbatim if the marketer picks this card. Should read like something a marketer would actually ask.",
    ),
  parentModelId: z
    .string()
    .nullable()
    .describe(
      "The Customer Studio parent model id (as a string) this prompt revolves around, or null. Set this ONLY when the prompt heavily centers on a specific Customer Studio schema parent model (not a general model or rETL model). Use the exact id from the workspace bundle's Customer Studio parents; otherwise return null.",
    ),
  icon: z
    .enum(SUGGESTION_ICONS)
    .describe(
      "The icon to associate with the card, for visual flair only. Required — every card must have one. Pick the best-matching icon after constructing the suggestion; don't let it influence the suggestion itself.",
    ),
});

export const suggestionCategorySchema = z.object({
  category: z
    .enum(SUGGESTION_CATEGORIES)
    .describe("Which of the three fixed categories these cards belong to."),
  cards: z
    .array(suggestionCardSchema)
    .min(1)
    .describe(
      "Suggestion cards for this category. Return at least 3 high-quality cards per category; favor quality over padding, but a category with fewer than 3 strong cards should be omitted entirely rather than included thin.",
    ),
});

export const suggestionBatchResponseSchema = z
  .object({
    categories: z
      .array(suggestionCategorySchema)
      .describe(
        "One entry per category that has at least one card. Each category may appear at most once. Omit a category entirely if the workspace lacks evidence to support any strong suggestion for it. Return the complete set of suggestions across all categories in a single response — never one suggestion or one category at a time.",
      ),
    noData: z
      .boolean()
      .optional()
      .describe(
        "Set to true ONLY when the workspace genuinely has no usable data or you otherwise cannot generate suggestions. When true, `categories` may be empty. Demo/sandbox/internal/QA workspaces are NOT 'no data' — generate suggestions for those anyway.",
      ),
    noDataReason: z
      .string()
      .nullable()
      .optional()
      .describe(
        "When `noData` is true, a short human-readable explanation of why no suggestions could be generated. Otherwise null.",
      ),
  })
  // Categories are keyed by name when persisted, so a duplicate category would
  // silently clobber the earlier bucket. Reject it instead — the uniqueness
  // check can't be expressed in JSON Schema, so it runs at parse time.
  .superRefine((value, ctx) => {
    const seen = new Set<string>();
    for (const entry of value.categories) {
      if (seen.has(entry.category)) {
        ctx.addIssue({
          code: "custom",
          message: `Duplicate category "${entry.category}"; each category may appear at most once.`,
          path: ["categories"],
        });
      }
      seen.add(entry.category);
    }
  });

export type ParsedSuggestion = z.infer<typeof suggestionCardSchema>;
export type SuggestionCategoryResult = z.infer<typeof suggestionCategorySchema>;
export type SuggestionBatchResponse = z.infer<
  typeof suggestionBatchResponseSchema
>;
